import { eAtlas, eUIAtlasFrame, Game } from "../../Game";
import { ScaleManager } from "../../Utils/ScaleManager";
import { AtlasUtils } from "../../Utils/Atlas/AtlasUtils";
import { Gameplay, TIMER } from "../Gameplay";
import { Ball } from "../Player/Ball";
import { RoundManager } from "../Round/RoundManager";
import { TargetManager } from "../Targets/TargetManager";
import { SwipeDisplay } from "./SwipeDisplay";



export interface TutorialSettings {
    /**
     * Vrchol zakrivani v hernim svete.
     */
    bendVertex?: pc.Vec3;
}



const enum eTutorialState {
    off,
    delayBeforeMove,
    move,
    delayAfterMove,
    delayBeforeRep
}



class TutorialPathPart {
    /**
     * (RO) Delka useku
     */
    get len(): number { return this._len; }
    private _len: number;

    get start(): pc.Vec2 { return this._startPos; }
    private _startPos: pc.Vec2;

    get endPos(): pc.Vec2 { return this._endPos; }
    private _endPos: pc.Vec2;

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    constructor(startPos: pc.Vec2, endPos: pc.Vec2) {
        this._startPos = startPos;
        this._endPos = endPos;
        this._len = startPos.distance(endPos);
    }

    //-------------------------------------------------------------------------
    /**
     * Ziskat pozici v useku
     * @param dis Vzdalenost od zacatku useku (nesmi byt vetsi nez celkova delka useku)
     * @param output Vystupni pozice
     */
    //-------------------------------------------------------------------------
    getPos(dis: number, output: pc.Vec2): void {
        output.lerp(this._startPos, this._endPos, dis / this._len);
    }
}



export class Tutorial {
    /**
     * Rychlost pohybu ruky v pixelech za vterinu
     */
    private static readonly HAND_MOVE_SPEED = 500;
    private static readonly HAND_WIDTH = 196;
    private static readonly HAND_HEIGHT = 191;

    private static readonly PATH_DOTS_OPACITY = 0.4;

    private _state: eTutorialState;
    private _stateTime: number;

    private _settings: TutorialSettings;

    private _handEnt: pc.Entity;
    private _handPos = new pc.Vec2();
    private _swipeDisplay: SwipeDisplay;

    private _path: TutorialPathPart[];
    private _pathPartId: number;
    private _pathPartStartDis: number;

    private _processFncs: (() => void)[];

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    constructor(screen: pc.Entity) {
        // Process funkce pro jednotlive stavy
        this._processFncs = [];
        this._processFncs[eTutorialState.delayBeforeMove] = this.processDelayBeforeMove;
        this._processFncs[eTutorialState.move] = this.processMove;
        this._processFncs[eTutorialState.delayAfterMove] = this.processDelayAfterMove;
        this._processFncs[eTutorialState.delayBeforeRep] = this.processDelayBeforeRep;

        // Ruka
        let atlas = Game.getAtlas(eAtlas.ui);
        let rc = AtlasUtils.getAtlasFrameRegion(atlas, eUIAtlasFrame.tutHand);

        this._handEnt = new pc.Entity();
        this._handEnt.addComponent(pc.eCompomentType.element, {
            type: pc.ELEMENTTYPE_IMAGE,
            texture: atlas.texture,
            width: Tutorial.HAND_WIDTH,
            height: Tutorial.HAND_HEIGHT,
            anchor: [0, 1, 0, 1],
            pivot: [0.05, 0.95],
            rect: rc,
        });
        this._handEnt.enabled = false;
        screen.addChild(this._handEnt);

        // Vykresleni trasy
        this._swipeDisplay = new SwipeDisplay();

        // Stav
        this._state = eTutorialState.off;
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    reset(): void {
        if (this._state != eTutorialState.off) {
            // Srkyt ruku a zobrazeni swipu
            this._handEnt.enabled = false;
            this._swipeDisplay.reset();

            // Ukoncit zachytavani vykopnuti mice
            Gameplay.instance.events.off(Ball.EVENT_KICKED_OFF1, this.handleBallKickOff, this);

            // Ukoncit zachytavani zmeny rozliseni
            Game.app.off(ScaleManager.EVENT_RESIZE, this.handleResChange, this);

            // Stav
            this._state = eTutorialState.off;
        }
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    show(settings: TutorialSettings): void {
        this._settings = settings;

        // Zachytavat zmenu rozliseni
        Game.app.on(ScaleManager.EVENT_RESIZE, this.handleResChange, this);

        // Zachytavat vykopnuti mic
        Gameplay.instance.events.on(Ball.EVENT_KICKED_OFF1, this.handleBallKickOff, this);

        // Nastavit cestup swipu
        this.calculateHandPath();

        // Zobrazit ruku na zacatku cesty
        this.start();
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    update(): void {
        if (this._state == eTutorialState.off)
            return;

        this._processFncs[this._state].call(this);
    }

    //-------------------------------------------------------------------------
    /**
     * Zahajit zobrazeni tutorialu
     */
    //-------------------------------------------------------------------------
    private start(): void {
        this._pathPartId = 0;
        this._pathPartStartDis = 0;

        this._path[0].getPos(0, this._handPos);
        this._handEnt.setLocalPosition(this._handPos.x, this._handPos.y, 0);
        this._handEnt.enabled = true;

        this._swipeDisplay.reset();
        this._swipeDisplay.opacity = Tutorial.PATH_DOTS_OPACITY;

        this._state = eTutorialState.delayBeforeMove;
        this._stateTime = TIMER.time;
    }

    //-------------------------------------------------------------------------
    /**
     * Vypocitat cestu ruky podle nastaveni tutorialu
     */
    //-------------------------------------------------------------------------
    private calculateHandPath(): void {
        let screenPos = new pc.Vec3();
        let startPos: pc.Vec2;

        this._path = [];

        // Ziskat pozici vykopu
        this.addPathVertex(RoundManager.instance.roundSetup.kickOffPos, screenPos);
        startPos = new pc.Vec2(screenPos.x, screenPos.y);

        // Vrchol zakrivani
        if (this._settings.bendVertex) {
            this.addPathVertex(this._settings.bendVertex, screenPos);
            this._path.push(new TutorialPathPart(startPos, new pc.Vec2(screenPos.x, screenPos.y)));
            startPos = this._path[0].endPos;
        }

        // Ziskat pozici hlavniho terce
        this.addPathVertex(TargetManager.instance.mainTarget.container.getPosition(), screenPos);
        this._path.push(new TutorialPathPart(startPos, new pc.Vec2(screenPos.x, screenPos.y)));
    }

    //-------------------------------------------------------------------------
    /**
     * Prodleva pred zahajenim pohybu ruky
     */
    //-------------------------------------------------------------------------
    private processDelayBeforeMove(): void {
        if (TIMER.time - this._stateTime > 750) {
            this._swipeDisplay.addPoint(this._handPos.x, this._handPos.y, 0);

            this._state = eTutorialState.move;
            this._stateTime = TIMER.time;
        }
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    private processMove(): void {
        let dis = ((TIMER.time - this._stateTime) / 1000) * Tutorial.HAND_MOVE_SPEED;
        let pathPart = this._path[this._pathPartId];

        if (dis >= this._pathPartStartDis + pathPart.len) {
            if (++this._pathPartId == this._path.length) {
                // Dosazeno konce swipu
                dis = this._pathPartStartDis + pathPart.len;

                this._state = eTutorialState.delayAfterMove;
                this._stateTime = TIMER.time;
            }
            else {
                this._pathPartStartDis += pathPart.len;
                pathPart = this._path[this._pathPartId];
            }
        }

        // Ziskat aktualni pozici ruky
        pathPart.getPos(dis - this._pathPartStartDis, this._handPos);
        this._handEnt.setLocalPosition(this._handPos.x, this._handPos.y, 0);

        // Zobrazeni cesty
        this._swipeDisplay.addPoint(this._handPos.x, this._handPos.y, dis);
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    private processDelayAfterMove(): void {
        if (TIMER.time - this._stateTime > 750) {
            this._handEnt.enabled = false;

            this._state = eTutorialState.delayBeforeRep;
            this._stateTime = TIMER.time;
        }
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    private processDelayBeforeRep(): void {
        if (TIMER.time - this._stateTime > 1000) {
            this.start();
        }
        else {
            // Mizeni tecek cesty            
            this._swipeDisplay.opacity = Tutorial.PATH_DOTS_OPACITY - Tutorial.PATH_DOTS_OPACITY * pc.SineIn((TIMER.time - this._stateTime) / 500);
        }
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    private addPathVertex(worldPos: pc.Vec3, screenPos: pc.Vec3): void {
        // Prevest pozici v hernim svete na pozici na obrazovce
        Game.camera.camera.worldToScreen(worldPos, screenPos);

        screenPos.mulScalar(Game.scale.internalToOuputResRatio);
        screenPos.y *= -1;
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    private handleBallKickOff(): void {
        // Ukoncit zobrazeni tutorialu
        this.reset();
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    private handleResChange(w: number, h: number): void {
        // Prepocitat cestu swipu
        this.calculateHandPath();

        // Zobrazit tutorial od zacatku (jinak se tecky swipu budou zobrazovat na spatnych pozicich)
        this.start();
    }
}